import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  getStudents as get,
  createStudent as create,
  updateStudent as update,
  changeProfilePicture as changePicture,
  deleteStudent as remove,
  updateClass,
  getStudentsBeenInClass as getBeenInClass,
  updateGraduationDate as updateGraduation,
} from "services/studentServices";
import {
  uploadTrainingPicture,
  updateTrainingPicture as updatePicture,
  deleteTrainingPicture,
} from "services/trainingPictureServices";
import {
  StudentSortValue,
  TrainingPictureOperation,
} from "config/constants";
import { Student, StudentFormData, UpdateClassFormData } from "types/Student";
import type { UpdatedTrainingPicture } from "types/TrainingPicture";
import { getRelativeProfilePicturePath } from "utilities/StudentProfilePicture";

const getStudents = createAsyncThunk<
  { students: Student[] },
  { classId: number; sortValue: StudentSortValue },
  { rejectValue: string }
>(
  "students/getStudents",
  async ({ classId, sortValue }, { rejectWithValue }) => {
    try {
      const students = await get(classId, sortValue);
      return { students };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

const createStudent = createAsyncThunk<
  { student: Student },
  { formData: StudentFormData; trainingPictures: File[] },
  { rejectValue: string }
>(
  "students/createStudent",
  async ({ formData, trainingPictures }, { rejectWithValue }) => {
    try {
      let student = await create(formData);
      let profilePictureUrl: string | null = null;
      for (let i = 0; i < trainingPictures.length; i++) {
        const uploadedPicture = await uploadTrainingPicture(
          student.id,
          trainingPictures[i]
        );
        if (i === 0) {
          profilePictureUrl = uploadedPicture.original_picture;
        }
      }
      if (profilePictureUrl) {
        student = await changePicture(
          student.id,
          getRelativeProfilePicturePath(profilePictureUrl)
        );
      }
      return { student };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

const createMultipleStudents = createAsyncThunk<
  { students: Student[] },
  { formData: StudentFormData[] },
  { rejectValue: string }
>(
  "students/createMultipleStudent",
  async ({ formData }, { rejectWithValue }) => {
    try {
      const createdStudents = [];
      for (let studentFormData of formData) {
        try {
          const student = await create(studentFormData);
          createdStudents.push(student);
        } catch (e) {
          for (let student of createdStudents) {
            await remove(student.id);
          }
          throw new Error("_student_form._file_error_content");
        }
      }
      return { students: createdStudents };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

const updateStudent = createAsyncThunk<
  { student: Student },
  {
    id: number;
    formData: StudentFormData;
    trainingPictures: File[];
    updatedTrainingPictures: UpdatedTrainingPicture[];
  },
  { rejectValue: string }
>(
  "students/updateStudent",
  async (
    { id, formData, trainingPictures, updatedTrainingPictures },
    { rejectWithValue }
  ) => {
    try {
      let student = await update(id, formData);
      await updateTrainingPicture(updatedTrainingPictures);
      let profilePictureUrl: string | null = null;
      for (let i = 0; i < trainingPictures.length; i++) {
        const uploadedPicture = await uploadTrainingPicture(
          student.id,
          trainingPictures[i]
        );
        if (i === 0) {
          profilePictureUrl = uploadedPicture.original_picture;
        }
      }
      if (!student.profile_picture_for_admin && profilePictureUrl) {
        student = await changePicture(
          student.id,
          getRelativeProfilePicturePath(profilePictureUrl)
        );
      }
      return { student };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);
const updateTrainingPicture = async (
  updatedTrainingPictures: UpdatedTrainingPicture[]
) => {
  try {
    for (let picture of updatedTrainingPictures) {
      switch (picture.operation) {
        case TrainingPictureOperation.use:
          await updatePicture(picture.id, true);
          break;
        case TrainingPictureOperation.unuse:
          await updatePicture(picture.id, false);
          break;
        case TrainingPictureOperation.delete:
          await deleteTrainingPicture(picture.id);
          break;
        default:
          break;
      }
    }
  } catch (e) {
    throw e;
  }
};

const updateClassForStudent = createAsyncThunk<
  { formData: UpdateClassFormData },
  { formData: UpdateClassFormData },
  { rejectValue: string }
>("students/updateClass", async ({ formData }, { rejectWithValue }) => {
  try {
    await updateClass(formData);
    return { formData };
  } catch (e) {
    return rejectWithValue((e as Error).message);
  }
});

const changeProfilePicture = createAsyncThunk<
  { student: Student },
  { id: number; picture: string },
  { rejectValue: string }
>(
  "students/changeProfilePicture",
  async ({ id, picture }, { rejectWithValue }) => {
    try {
      const student = await changePicture(id, picture);
      return { student };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

const deleteStudent = createAsyncThunk<
  { id: number },
  { id: number },
  { rejectValue: string }
>("students/deleteStudent", async ({ id }, { rejectWithValue }) => {
  try {
    await remove(id);
    return { id };
  } catch (e) {
    return rejectWithValue((e as Error).message);
  }
});

const getStudentsBeenInClass = createAsyncThunk<
  { students: Student[] },
  { classId: number },
  { rejectValue: string }
>(
  "students/getStudentsBeenInClass",
  async ({ classId }, { rejectWithValue }) => {
    try {
      const students = await getBeenInClass(classId);
      return { students };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

const updateGraduationDate = createAsyncThunk<
  { student: Student },
  { id: number; graduationDate: string },
  { rejectValue: string }
>(
  "students/updateGraduationDate",
  async ({ id, graduationDate }, { rejectWithValue }) => {
    try {
      const student = await updateGraduation(id, graduationDate);
      return { student };
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }
  }
);

export {
  getStudents,
  createStudent,
  createMultipleStudents,
  updateStudent,
  updateClassForStudent,
  changeProfilePicture,
  deleteStudent,
  getStudentsBeenInClass,
  updateGraduationDate,
};
